import { useOfflineSync } from '../../contexts/OfflineSyncContext.jsx';
import { useConnectivity } from '../../contexts/ConnectivityContext.jsx';
import { OFFLINE_INTERACTION_ENABLED } from '../../offline/offlineConfig.js';
import './SyncStatusBanner.css';

function formatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Nomme l'action refusée pour que l'utilisateur sache laquelle n'a pas été
// enregistrée (F-07) — le libellé de la demande est posé à la mise en file.
function describeRefusedAction(row) {
  const label = row?.payload?.label;
  const suffix = label ? ` — ${label}` : '';
  switch (row?.type) {
    case 'fonds.markSent':
      return `« J'ai envoyé »${suffix}`;
    case 'fonds.confirmReceived':
      return `« J'ai reçu »${suffix}`;
    case 'proof.upload':
      return 'Photo de preuve';
    default:
      return 'Mise à jour du parcours';
  }
}

export default function SyncStatusBanner() {
  const {
    pendingCount,
    errorCount,
    refusedActions = [],
    dismissRefused,
    lastSyncAt,
    syncing,
    lastSyncError,
    runSync,
    retryErrored,
  } = useOfflineSync();
  const { isReachable } = useConnectivity();

  if (!OFFLINE_INTERACTION_ENABLED) return null;
  if (
    pendingCount === 0 &&
    errorCount === 0 &&
    refusedActions.length === 0 &&
    isReachable &&
    !lastSyncError
  ) {
    return null;
  }

  const handleSync = async () => {
    if (!isReachable || syncing) return;
    await runSync();
  };

  const handleRetryErrored = async () => {
    if (!isReachable || syncing) return;
    await retryErrored();
  };

  return (
    <div
      className={`sync-status-banner ${isReachable ? '' : 'sync-status-banner--offline'}`}
      role="status"
      aria-live="polite"
    >
      <div className="sync-status-banner-inner">
        {!isReachable && (
          <span className="sync-status-banner-text">
            Hors ligne — vos modifications sont enregistrées sur cet appareil.
          </span>
        )}
        {pendingCount > 0 && (
          <span className="sync-status-banner-text">
            {pendingCount} action{pendingCount > 1 ? 's' : ''} en attente de synchronisation
            {lastSyncAt ? ` · Dernière sync ${formatTime(lastSyncAt)}` : ''}
          </span>
        )}
        {errorCount > 0 && (
          <span className="sync-status-banner-error">
            {errorCount} action{errorCount > 1 ? 's' : ''} en échec après plusieurs tentatives.
          </span>
        )}
        {refusedActions.map((row) => (
          <span key={row.id} className="sync-status-banner-refused">
            <span className="sync-status-banner-error">
              Refusé par le serveur : {describeRefusedAction(row)}. {row.errorMessage}
            </span>
            <button
              type="button"
              className="btn-secondary btn-sm sync-status-banner-btn"
              onClick={() => dismissRefused?.(row.id)}
            >
              Compris
            </button>
          </span>
        ))}
        {lastSyncError && isReachable && errorCount === 0 && (
          <span className="sync-status-banner-error">{lastSyncError}</span>
        )}
        {isReachable && pendingCount > 0 && (
          <button
            type="button"
            className="btn-secondary btn-sm sync-status-banner-btn"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Synchronisation…' : 'Synchroniser'}
          </button>
        )}
        {isReachable && errorCount > 0 && (
          <button
            type="button"
            className="btn-secondary btn-sm sync-status-banner-btn"
            onClick={handleRetryErrored}
            disabled={syncing}
          >
            {syncing ? 'Synchronisation…' : 'Réessayer'}
          </button>
        )}
      </div>
    </div>
  );
}
